import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useMutation } from '@apollo/client';
import { UPDATE_PROFILE_INFO } from 'pages/ProfileSettingsPage/gqlSchemes/updateProfileInfo';
import { UPDATE_SELECTED_LANGUAGE } from './gqlSchemes/updateSelectedLanguage';
import { selectors as userSelectors } from '../../../services/user';

import { Box as MuiBox, Chip as MuiChip } from '@mui/material';

const languageOptions = [
  {
    label: 'English',
    code: 'en',
    flagCode: 'us',
  },
  {
    label: 'German',
    code: 'de',
    flagCode: 'de',
  },
  // {
  //   label: 'Dutch',
  //   code: 'nl',
  //   flagCode: "nl",
  // },
  {
    label: 'Indonesian',
    code: 'id',
    flagCode: 'id',
  },
  {
    label: 'Portuguese (BR)',
    code: 'pt-br',
    flagCode: 'br',
  },
  {
    label: 'Arabic (SA)',
    code: 'ar-eg',
    flagCode: 'sa',
  },
  {
    label: 'French',
    code: 'fr',
    flagCode: 'fr',
  },
  {
    label: 'Spanish (MX)',
    code: 'es-mx',
    flagCode: 'mx',
  },
];

const LanguageSwitch = () => {
  const { i18n, t } = useTranslation();

  const { preferredLanguages, selectedLanguage } = useSelector(
    userSelectors.getUser
  );

  const userLang =
    selectedLanguage || preferredLanguages[0] || i18n.language.toLowerCase();

  const defaultLang =
    languageOptions.find((l) => l.code === userLang) || languageOptions[0];
  const [curLang, setCurLang] = useState(defaultLang);

  // const [updateProfileInfo] = useMutation(UPDATE_PROFILE_INFO);
  const [updateSelectedLanguage] = useMutation(UPDATE_SELECTED_LANGUAGE);

  const handlePick = async (value) => {
    setCurLang(value);
    await i18n.changeLanguage(
      value.code.includes('-')
        ? value.code.split('-')[0] +
            '-' +
            value.code.split('-')[1].toUpperCase()
        : value.code
    );

    try {
      const res = await updateSelectedLanguage({
        variables: {
          record: {
            selectedLanguage: value.code,
          },
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (i18n.language.toLowerCase() !== userLang)
      i18n.changeLanguage(
        userLang
          ? userLang.includes('-')
            ? userLang.split('-')[0] +
              '-' +
              userLang.split('-')[1].toUpperCase()
            : userLang
          : 'en'
      );
  }, []);

  return (
    <WrapperSMuiBox data-id="language-switch-open-dialog">
      <SMuiBox>
        <img
          src={`https://flagcdn.com/w20/${curLang?.flagCode || 'us'}.webp`}
          width="20"
          height="15"
          alt={curLang?.label || 'us'}
        />
      </SMuiBox>
      <LightTooltip className="light-tooltip">
        <ListSMuiBox data-id="language-element-list">
          {languageOptions.map((lang, idx) => (
            <MuiBox width="100%" key={idx} mt={1}>
              <SMuiChip
                // active={curLang.code === lang.code}
                onClick={() => handlePick(lang)}
                icon={
                  <img
                    src={`https://flagcdn.com/w20/${lang.flagCode}.webp`}
                    width="20"
                    height="15"
                    alt={lang.label}
                  />
                }
                label={t(lang.label)}
              />
            </MuiBox>
          ))}
        </ListSMuiBox>
      </LightTooltip>
    </WrapperSMuiBox>
  );
};

const WrapperSMuiBox = styled(MuiBox)`
  && {
    position: relative;
    &:hover .light-tooltip {
      z-index: 99999999;
      opacity: 1;
      transform: translate(0);
    }
  }
`;

const ListSMuiBox = styled(MuiBox)`
  && {
    display: flex;
    align-items: center;
    flex-direction: column;
    height: 175px;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
  }
`;

const LightTooltip = styled.div`
  && {
    z-index: -1;
    opacity: 0;
    position: absolute;
    box-sizing: border-box;
    left: 80%;
    width: 235px;
    transform: translate(9999px);
    bottom: 0;
    background: ${({ theme }) => theme.palette.colors.white};
    padding: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    transition: opacity 0.3s;
    animation-delay: 0.1s;
  }
`;

const SMuiBox = styled(MuiBox)`
  && {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 50px;
    width: 100%;
    padding: 10px 0;
    cursor: pointer;
    &:hover img {
      box-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
    }
  }
`;

const SMuiChip = styled(MuiChip)`
  && {
    height: 45px;
    padding-left: 10px;
    cursor: pointer;
    border-radius: 5px;
    transition: all 0.3s;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background: ${({ active, theme }) =>
      active ? theme.palette.colors.blue200 : 'transparent'};
    .MuiChip-label {
      transition: all 0.3s;
      font-size: 14px;
      font-weight: 600;
      margin-left: 5px;
      color: ${({ active, theme }) =>
        active ? theme.palette.colors.black100 : theme.palette.colors.black100};
    }
    &:hover {
      .MuiChip-label {
        color: ${({ theme }) => theme.palette.colors.black100};
      }
    }
  }
`;

export default LanguageSwitch;
